import { useState, useEffect, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Loader2 } from "lucide-react";

const EMPTY = { name: "", pin: "", role: "cashier", active: true };

export function StaffManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [cap, setCap] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    opApi.get("/tenant/employees").then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    opApi.get("/tenant/entitlements").then((r) => setCap(r.data?.usage?.max_team_members?.cap ?? null)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ name: e.name, pin: "", role: e.role, active: e.active }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    if (!editId && !/^\d{4,6}$/.test(form.pin)) { toast.error("PIN must be 4–6 digits."); return; }
    if (editId && form.pin && !/^\d{4,6}$/.test(form.pin)) { toast.error("PIN must be 4–6 digits."); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { name: form.name.trim(), role: form.role, active: form.active };
        if (form.pin) payload.pin = form.pin;
        await opApi.put(`/tenant/employees/${editId}`, payload);
      } else {
        await opApi.post("/tenant/employees", { name: form.name.trim(), pin: form.pin, role: form.role });
      }
      toast.success("Saved."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.name}?`)) return;
    try { await opApi.delete(`/tenant/employees/${e.id}`); load(); } catch (er) { toast.error(apiError(er.response?.data?.detail)); }
  };

  return (
    <div data-testid="staff-manager">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground max-w-lg">Staff sign in at the Register with a name + PIN. Cashiers ring sales &amp; manage customers; managers can also edit the menu and see totals. <span className="font-medium text-foreground" data-testid="staff-usage">{rows.length}{cap != null ? (cap === "unlimited" ? " · Unlimited seats" : ` of ${cap} used`) : ""}</span></p>
        <Button size="sm" className="rounded-none gap-1.5" onClick={openNew} data-testid="staff-add-btn"><UserPlus className="h-4 w-4" /> Add staff</Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground flex items-center gap-2 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        : rows.length === 0 ? <div className="border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="staff-empty">No staff yet. Add a cashier so they can clock in at the Register.</div>
        : (
        <div className="border border-border divide-y divide-border">
          {rows.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`staff-row-${e.id}`}>
              <div className="flex-1 min-w-0"><span className="text-sm font-medium">{e.name}</span></div>
              <Badge variant="outline" className="rounded-none capitalize">{e.role}</Badge>
              {!e.active && <Badge variant="outline" className="rounded-none text-muted-foreground">inactive</Badge>}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(e)} data-testid={`staff-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(e)} data-testid={`staff-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="staff-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit staff" : "Add staff"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
            <div><Label className="text-xs">{editId ? "New PIN (leave blank to keep)" : "PIN (4–6 digits)"}</Label>
              <Input className="rounded-none mt-1" inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} data-testid="staff-pin" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cashier">Cashier</SelectItem><SelectItem value="manager">Manager</SelectItem></SelectContent>
              </Select>
            </div>
            {editId && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={save} disabled={saving} data-testid="staff-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
